import { useEffect } from 'react'
import { Phone, ArrowRight, Bot } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatPhoneDisplay } from '@/lib/utils'
import type { PhoneNumber } from './types'

interface BuilderPhasePhoneProps {
  phoneNumbers: PhoneNumber[]
  selectedPhoneId: string
  onSelectPhone: (id: string) => void
  onContinue: () => void
  onSkip: () => void
}

export default function BuilderPhasePhone({
  phoneNumbers,
  selectedPhoneId,
  onSelectPhone,
  onContinue,
  onSkip,
}: BuilderPhasePhoneProps) {
  // Pre-select the first available number so assignment is the obvious
  // default. Without this, the dropdown lands on "Don't assign a number
  // yet", which reads like opting out of voice on agent creation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (phoneNumbers.length > 0 && !selectedPhoneId) {
      onSelectPhone(phoneNumbers[0].id)
    }
  }, [])

  const hasNumbers = phoneNumbers.length > 0

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Phone className="h-4 w-4 text-brand-royal" />
        <p className="text-sm font-medium">Assign a phone number</p>
      </div>

      {hasNumbers ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Pick the number this agent will answer on. You can change this later in AI Agents.
          </p>
          <Select value={selectedPhoneId || 'none'} onValueChange={onSelectPhone}>
            <SelectTrigger>
              <SelectValue placeholder="Select a number" />
            </SelectTrigger>
            <SelectContent>
              {phoneNumbers.map(p => (
                <SelectItem key={p.id} value={p.id}>{formatPhoneDisplay(p.phoneNumber)}</SelectItem>
              ))}
              <SelectItem value="none">Don't assign a number yet</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="rounded-lg border border-brand-royal/20 bg-brand-royal/5 p-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-brand-royal shrink-0" />
            <p className="text-sm font-medium">No phone number yet</p>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Skip this step for now. You can add a number anytime from <strong className="font-semibold text-foreground">AI Agents</strong> — click <strong className="font-semibold text-foreground">Add Number</strong> on the canvas, then return here to assign it to this agent.
          </p>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button className="flex-1" onClick={onContinue}>
          Continue <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <Button variant="ghost" onClick={onSkip} className="text-muted-foreground">
          Skip for now
        </Button>
      </div>
    </div>
  )
}
